import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { Save } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { SettingsLayout } from '../components/settings-layout'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Select } from '../components/ui/select'
import { api } from '../lib/api'
import { notify } from '../lib/notify'
import { Route as rootRoute } from './__root'

/** /settings ohne Unterpfad war eine 404 (E2E-Fund W1) — leite auf den
 *  ersten Settings-Bereich um. */
export const SettingsIndexRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: () => <Navigate to="/settings/profile" replace />,
})

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/profile',
  component: ProfilePage,
})

function ProfilePage() {
  const queryClient = useQueryClient()
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const { data: groupsData } = useQuery({
    queryKey: ['nonbusinessgroups'],
    queryFn: () => api.listNonbusinessgroups(),
  })
  const { data: rulesData } = useQuery({
    queryKey: ['break-rules'],
    queryFn: () => api.listBreakRules(),
  })

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [holidayGroup, setHolidayGroup] = useState<string>('')
  const [breakRule, setBreakRule] = useState<string>('')
  const [startPage, setStartPage] = useState<string>('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  useEffect(() => {
    if (meData?.user) {
      setName(meData.user.name)
      setEmail(meData.user.email)
      setHolidayGroup(meData.user.nonbusinessgroupId ?? '')
      setBreakRule(meData.user.breakRuleId ?? '')
      setStartPage(meData.user.startPage ?? '')
    }
  }, [meData])

  const saveProfile = useMutation({
    mutationFn: () =>
      api.updateProfile({
        name,
        email,
        nonbusinessgroupId: holidayGroup || null,
        breakRuleId: breakRule || null,
        startPage: startPage || null,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['auth', 'me'], data)
      queryClient.invalidateQueries({ queryKey: ['reports', 'weekly'] })
      notify.success('Profil aktualisiert')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const changePassword = useMutation({
    mutationFn: () => api.updateProfile({ currentPassword, newPassword }),
    onSuccess: () => {
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      notify.success('Passwort geändert')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />

  const onSaveProfile = (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !email.trim()) {
      notify.error('Name und E-Mail sind Pflicht')
      return
    }
    saveProfile.mutate()
  }

  const onChangePassword = (e: FormEvent) => {
    e.preventDefault()
    if (!currentPassword || !newPassword) {
      notify.error('Aktuelles und neues Passwort eingeben')
      return
    }
    if (newPassword.length < 8) {
      notify.error('Neues Passwort muss mind. 8 Zeichen haben')
      return
    }
    if (newPassword !== confirmPassword) {
      notify.error('Passwort-Bestätigung stimmt nicht')
      return
    }
    changePassword.mutate()
  }

  return (
    <SettingsLayout
      title="Profil"
      description="Allgemeine Daten, Bundesland für Feiertage, Passwort. Andere Sessions werden bei Passwort-Änderung beendet."
    >
      <div className="space-y-4">
        <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-6">
          <h2 className="text-lg font-medium mb-4">Allgemeine Daten</h2>
          <form onSubmit={onSaveProfile} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="prof-name">Name</Label>
                <Input id="prof-name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="prof-email">E-Mail</Label>
                <Input
                  id="prof-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="prof-holiday">Feiertagsgruppe (Bundesland)</Label>
                <Select
                  id="prof-holiday"
                  value={holidayGroup}
                  onChange={(e) => setHolidayGroup(e.target.value)}
                >
                  <option value="">— keine —</option>
                  {(groupsData?.groups ?? []).map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </Select>
                <p className="text-xs text-text-tertiary">
                  Feiertage werden im Kalender markiert; Soll-Stunden in der Wochenansicht werden an
                  diesen Tagen auf 0 (bzw. 50% bei Halbtagen) reduziert.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="prof-breakrule">Pausenregel</Label>
                <Select
                  id="prof-breakrule"
                  value={breakRule}
                  onChange={(e) => setBreakRule(e.target.value)}
                >
                  <option value="">— Standard (DE: 30 min ab 6h, 45 min ab 9h) —</option>
                  {(rulesData?.rules ?? []).map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </Select>
                <p className="text-xs text-text-tertiary">
                  Steuert die Pflichtpausen-Anzeige im Kalender. Bei „Standard“ gilt ArbZG §4.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="prof-startpage">Startseite</Label>
                <Select
                  id="prof-startpage"
                  value={startPage}
                  onChange={(e) => setStartPage(e.target.value)}
                >
                  <option value="">Dashboard (Standard)</option>
                  <option value="/calendar">Kalender</option>
                  <option value="/time-accounts">Stundenkonten</option>
                  <option value="/customers">Kunden</option>
                  <option value="/projects">Projekte</option>
                  <option value="/invoices">Rechnungen</option>
                  <option value="/documents">Dateien</option>
                  <option value="/mail">Mail</option>
                  <option value="/chat">Chat</option>
                  <option value="/tickets">Tickets</option>
                  <option value="/kanban">Kanban</option>
                  <option value="/team-planning">Teamplanung</option>
                </Select>
                <p className="text-xs text-text-tertiary">
                  Welche Seite nach dem Login zuerst angezeigt wird.
                </p>
              </div>
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={saveProfile.isPending}>
                <Save className="h-4 w-4" /> Speichern
              </Button>
            </div>
          </form>
        </section>

        <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-6">
          <h2 className="text-lg font-medium mb-1">Passwort ändern</h2>
          <p className="text-sm text-text-tertiary mb-4">
            Andere Sessions werden bei Änderung beendet (deine aktuelle bleibt aktiv).
          </p>
          <form onSubmit={onChangePassword} className="space-y-4 max-w-md">
            <div className="space-y-2">
              <Label htmlFor="cur-pw">Aktuelles Passwort</Label>
              <Input
                id="cur-pw"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-pw">Neues Passwort</Label>
              <Input
                id="new-pw"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="conf-pw">Wiederholen</Label>
              <Input
                id="conf-pw"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={changePassword.isPending}>
                Passwort ändern
              </Button>
            </div>
          </form>
        </section>
      </div>
    </SettingsLayout>
  )
}
